import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes, FaSpinner, FaPen } from 'react-icons/fa';
import { SessionCardProps } from './types';

/**
 * SessionCard sub-component
 * Displays a single session with edit/delete functionality
 */
export const SessionCard: React.FC<SessionCardProps> = memo(({
  session,
  isCurrent,
  isDeleting,
  isEditing,
  isSaving,
  editingName,
  onSelect,
  onDelete,
  onEditName,
  onSaveName,
  onCancelEdit,
  onEditingNameChange,
  formatDate
}) => {
  const { t } = useTranslation('training');

  return (
    <div
      className={`
        group relative rounded-lg cursor-pointer transition-all duration-150
        ${isCurrent
          ? 'bg-accent/70 border border-primary/20 shadow-sm ring-1 ring-primary/10'
          : 'bg-card border border-border hover:border-border/60 hover:shadow-sm'
        }
      `}
      onClick={onSelect}
    >
      {/* Left accent */}
      {isCurrent && (
        <div className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-primary" />
      )}

      <div className="p-3.5 pl-4">
        {/* Top row: name + actions */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => onEditingNameChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSaveName();
                    else if (e.key === 'Escape') onCancelEdit();
                  }}
                  className="flex-1 min-w-0 px-2.5 py-1 text-sm border border-primary/30 rounded-md bg-card focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
                  placeholder={t('sessionSelector.enterName')}
                  autoFocus
                  disabled={isSaving}
                  aria-label={t('sessionSelector.editName')}
                />
                <button
                  type="button"
                  onClick={onSaveName}
                  disabled={isSaving}
                  className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 flex-shrink-0"
                >
                  {isSaving ? <FaSpinner className="animate-spin text-[10px]" /> : <FaCheck className="text-[10px]" />}
                </button>
                <button
                  type="button"
                  onClick={onCancelEdit}
                  disabled={isSaving}
                  className="w-7 h-7 rounded-md flex items-center justify-center bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 transition-colors duration-150 flex-shrink-0"
                >
                  <FaTimes className="text-[10px]" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span
                  className="text-sm font-medium text-foreground truncate transition-colors duration-150"
                  title={session.sessionName || session.sessionId}
                >
                  {session.sessionName || `${session.sessionId.substring(0, 20)}...`}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditName();
                  }}
                  className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-all duration-150 flex-shrink-0"
                  title={t('sessionSelector.editName')}
                >
                  <FaPen className="text-[8px]" />
                </button>
                {isCurrent && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide bg-accent text-accent-foreground px-1.5 py-0.5 rounded flex-shrink-0">
                    {t('sessionSelector.current')}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Delete button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            disabled={isDeleting}
            className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-all duration-150 disabled:opacity-50 flex-shrink-0"
            title={t('sessionSelector.deleteSession')}
          >
            {isDeleting ? (
              <FaSpinner className="animate-spin text-[10px]" />
            ) : (
              <FaTimes className="text-[10px]" />
            )}
          </button>
        </div>

        {/* Metadata row */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>{session.createdAt ? formatDate(session.createdAt) : t('sessionSelector.noDate')}</span>
          <span className="w-px h-3 bg-border" />
          <span>{session.fileCount || 0} {t('sessionSelector.files')}</span>
          {session.nDat && session.nDat > 0 && (
            <>
              <span className="w-px h-3 bg-border" />
              <span>{session.nDat.toLocaleString()} {t('sessionSelector.dataPoints')}</span>
            </>
          )}
        </div>

      </div>
    </div>
  );
});

SessionCard.displayName = 'SessionCard';

export default SessionCard;
